import {
  ChatChannel,
  RACE_INFO,
  t,
  type ChatHistoryDataMsg,
  type PlayerProfileDataMsg,
  type FriendEntry,
  type RaceId,
} from '@viral-space/shared';
import { attachMobileCloseButton } from './ui-shared';

const COLORS = {
  primary: '#00ff41',
  secondary: '#ffb000',
  danger: '#ff4444',
  bg: 'rgba(0,8,0,0.96)',
  panelBg: 'rgba(0,12,0,0.85)',
  dim: 'rgba(0,255,65,0.3)',
  dimText: 'rgba(0,255,65,0.55)',
  rowHover: 'rgba(0,255,65,0.08)',
} as const;

const FONT = "'Share Tech Mono', monospace";
const GLOW = `0 0 6px ${COLORS.primary}, 0 0 12px rgba(0,255,65,0.3)`;
const BOX_GLOW = `0 0 8px rgba(0,255,65,0.4), inset 0 0 8px rgba(0,255,65,0.05)`;

const CHANNEL_COLORS: Partial<Record<ChatChannel, string>> = {
  [ChatChannel.Global]: '#ffe34d',
  [ChatChannel.Sector]: '#00ccff',
  [ChatChannel.Direct]: '#ff88ff',
};

const HISTORY_LIMIT = 50;
const MAX_LINES_PER_THREAD = 300;

/**
 * Thread key format:
 *   'global'        — the Global channel
 *   'sector'        — the (current) Sector channel
 *   'dm:<lowername>' — one Direct conversation
 */
type ThreadKey = string;

interface StoredLine {
  senderName: string;
  content: string;
  timestamp: number;
  fromLocal: boolean;
  premium?: boolean;
}

interface ThreadState {
  key: ThreadKey;
  channel: ChatChannel;
  /** Display name of the DM partner (Direct threads only). */
  partnerName?: string;
  lines: StoredLine[];
  /** Dedupe index so live messages and replayed history don't double up. */
  seen: Set<string>;
  unread: number;
  historyRequested: boolean;
  historyLoaded: boolean;
}

export interface ChatWindowCallbacks {
  sendMessage: (content: string, channel: ChatChannel, targetName?: string) => void;
  requestHistory: (channel: ChatChannel, partnerName?: string) => void;
  requestProfile: (name: string) => void;
  onAddFriend: (name: string) => void;
  getFriends: () => FriendEntry[];
}

/**
 * Full-window comms panel. Expands the corner chatbox into a two-pane
 * social hub:
 *
 *   - Left sidebar: channels (GLOBAL / SECTOR) + one entry per Direct
 *     conversation (friends and recent DM partners), with unread badges.
 *   - Right pane: the selected thread's transcript + compose input.
 *
 * Player callsigns in the transcript are clickable and open a profile
 * card (level, race, stats, online status) with ADD FRIEND / MESSAGE
 * actions, so players can grow their friends list straight from public
 * chat.
 *
 * On phones the two panes collapse into a two-step drill-down (chat
 * list → conversation with a BACK button) so each screen keeps
 * comfortable touch targets.
 */
export class ChatWindow {
  private container: HTMLElement;
  private isMobile: boolean;

  private root!: HTMLElement;
  private sidebar!: HTMLElement;
  private sidebarList!: HTMLElement;
  private main!: HTMLElement;
  private threadHeader!: HTMLElement;
  private messageArea!: HTMLElement;
  private inputRow!: HTMLElement;
  private input!: HTMLInputElement;
  private profileOverlay!: HTMLElement;

  private threads = new Map<ThreadKey, ThreadState>();
  private activeThread: ThreadKey | null = null;
  private localName = '';
  private _visible = false;
  /** Callsign whose profile card is currently open / loading. */
  private profileFor: string | null = null;

  callbacks: ChatWindowCallbacks | null = null;
  /** Fired on open/close so the host can toggle flight input. */
  onOpenChange: ((open: boolean) => void) | null = null;

  constructor(container: HTMLElement, opts: { isMobile?: boolean } = {}) {
    this.container = container;
    this.isMobile = !!opts.isMobile;
    this.ensureThread('global');
    this.ensureThread('sector');
    this.build();

    document.addEventListener('keydown', (e) => {
      if (!this._visible) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (this.profileFor !== null) {
          this.closeProfile();
        } else {
          this.close();
        }
      }
    }, true);
  }

  get visible(): boolean {
    return this._visible;
  }

  setLocalPlayer(name: string): void {
    this.localName = name;
  }

  /* ═══════════════ DOM scaffolding ═══════════════ */

  private el(
    tag: string,
    styles: Partial<CSSStyleDeclaration>,
    text?: string,
  ): HTMLElement {
    const e = document.createElement(tag);
    Object.assign(e.style, styles);
    if (text !== undefined) e.textContent = text;
    return e;
  }

  private build(): void {
    const positionStyles: Partial<CSSStyleDeclaration> = this.isMobile
      ? {
          inset: '0',
          // Full-screen takeover: an opaque backdrop so the HUD (health
          // bars, radar, credits) doesn't bleed through the transcript.
          background: '#000800',
          paddingTop: 'calc(8px + env(safe-area-inset-top, 0px))',
          paddingRight: 'calc(8px + env(safe-area-inset-right, 0px))',
          paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
          paddingLeft: 'calc(8px + env(safe-area-inset-left, 0px))',
        }
      : {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(860px, 94vw)',
          height: 'min(560px, 88vh)',
          padding: '14px 16px',
          border: `1px solid ${COLORS.dim}`,
          boxShadow: BOX_GLOW,
        };

    this.root = this.el('div', {
      position: 'absolute',
      display: 'none',
      flexDirection: 'column',
      gap: '8px',
      background: COLORS.bg,
      fontFamily: FONT,
      color: COLORS.primary,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      zIndex: '70',
      pointerEvents: 'auto',
      boxSizing: 'border-box',
      ...positionStyles,
    });
    this.container.appendChild(this.root);

    const header = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: `1px solid ${COLORS.dim}`,
      paddingBottom: '6px',
      flexShrink: '0',
    });
    header.appendChild(
      this.el('div', {
        fontSize: '16px',
        textShadow: GLOW,
        letterSpacing: '3px',
      }, t('chat.commsTitle')),
    );
    const closeBtn = this.makeBtn('X', () => this.close(), COLORS.danger);
    header.appendChild(closeBtn);
    this.root.appendChild(header);

    const body = this.el('div', {
      display: 'flex',
      flex: '1',
      minHeight: '0',
      gap: '10px',
    });
    this.root.appendChild(body);

    // ── Sidebar (chat list) ──
    this.sidebar = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      width: this.isMobile ? '100%' : '230px',
      flexShrink: '0',
      minHeight: '0',
    });
    this.sidebarList = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      overflowY: 'auto',
      minHeight: '0',
      paddingRight: '4px',
      touchAction: 'pan-y',
    });
    this.sidebarList.style.scrollbarWidth = 'thin';
    this.sidebarList.style.scrollbarColor = `${COLORS.dim} transparent`;
    this.sidebar.appendChild(this.sidebarList);
    body.appendChild(this.sidebar);

    // ── Main pane (transcript + input) ──
    this.main = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      flex: '1',
      minWidth: '0',
      minHeight: '0',
      gap: '6px',
      borderLeft: this.isMobile ? 'none' : `1px solid ${COLORS.dim}`,
      paddingLeft: this.isMobile ? '0' : '10px',
    });
    body.appendChild(this.main);

    this.threadHeader = this.el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexShrink: '0',
      minHeight: '28px',
    });
    this.main.appendChild(this.threadHeader);

    this.messageArea = this.el('div', {
      flex: '1',
      overflowY: 'auto',
      minHeight: '0',
      background: COLORS.panelBg,
      border: `1px solid ${COLORS.dim}`,
      padding: '8px 10px',
      display: 'flex',
      flexDirection: 'column',
      gap: '3px',
      touchAction: 'pan-y',
    });
    this.messageArea.style.scrollbarWidth = 'thin';
    this.messageArea.style.scrollbarColor = `${COLORS.dim} transparent`;
    this.main.appendChild(this.messageArea);

    this.inputRow = this.el('div', {
      display: 'flex',
      gap: '6px',
      flexShrink: '0',
    });
    this.input = document.createElement('input');
    this.input.type = 'text';
    this.input.maxLength = 200;
    this.input.placeholder = t('chat.inputPlaceholder');
    Object.assign(this.input.style, {
      flex: '1',
      fontFamily: FONT,
      fontSize: this.isMobile ? '16px' : '12px', // 16px stops iOS auto-zoom
      padding: '8px 10px',
      background: 'rgba(0,8,0,0.85)',
      border: `1px solid ${COLORS.dim}`,
      color: COLORS.primary,
      outline: 'none',
      letterSpacing: '0.5px',
      textTransform: 'none',
      caretColor: COLORS.primary,
      minWidth: '0',
    });
    this.input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        this.submit();
      }
    });
    this.inputRow.appendChild(this.input);
    this.inputRow.appendChild(
      this.makeBtn(t('chat.send'), () => this.submit(), COLORS.primary),
    );
    this.main.appendChild(this.inputRow);

    // ── Profile card overlay ──
    this.profileOverlay = this.el('div', {
      position: 'absolute',
      inset: '0',
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0,4,0,0.75)',
      zIndex: '10',
    });
    this.profileOverlay.addEventListener('click', (e) => {
      if (e.target === this.profileOverlay) this.closeProfile();
    });
    this.root.appendChild(this.profileOverlay);

    attachMobileCloseButton(this.root, () => this.close(), {
      hideOnMobile: [closeBtn],
    });
  }

  /* ═══════════════ Threads ═══════════════ */

  private ensureThread(
    key: ThreadKey,
    partnerName?: string,
  ): ThreadState {
    let thread = this.threads.get(key);
    if (!thread) {
      thread = {
        key,
        channel:
          key === 'global' ? ChatChannel.Global
          : key === 'sector' ? ChatChannel.Sector
          : ChatChannel.Direct,
        partnerName,
        lines: [],
        seen: new Set(),
        unread: 0,
        historyRequested: false,
        historyLoaded: false,
      };
      this.threads.set(key, thread);
    } else if (partnerName && !thread.partnerName) {
      thread.partnerName = partnerName;
    }
    return thread;
  }

  private dmKey(partnerName: string): ThreadKey {
    return `dm:${partnerName.trim().toLowerCase()}`;
  }

  private lineKey(line: StoredLine): string {
    return `${line.senderName.toLowerCase()}|${line.timestamp}|${line.content}`;
  }

  private pushLine(thread: ThreadState, line: StoredLine): boolean {
    const key = this.lineKey(line);
    if (thread.seen.has(key)) return false;
    thread.seen.add(key);
    thread.lines.push(line);
    if (thread.lines.length > MAX_LINES_PER_THREAD) {
      const dropped = thread.lines.splice(0, thread.lines.length - MAX_LINES_PER_THREAD);
      for (const d of dropped) thread.seen.delete(this.lineKey(d));
    }
    return true;
  }

  private selectThread(key: ThreadKey | null): void {
    this.activeThread = key;
    if (key) {
      const thread = this.threads.get(key);
      if (thread) {
        thread.unread = 0;
        if (!thread.historyRequested) {
          thread.historyRequested = true;
          this.callbacks?.requestHistory(thread.channel, thread.partnerName);
        }
      }
    }
    this.renderAll();
  }

  /* ═══════════════ Public API ═══════════════ */

  open(threadKey?: ThreadKey): void {
    const wasVisible = this._visible;
    this._visible = true;
    this.root.style.display = 'flex';
    if (threadKey !== undefined) {
      this.selectThread(threadKey);
    } else if (!wasVisible) {
      // Desktop lands on Global; phones land on the chat list.
      this.selectThread(this.isMobile ? null : this.activeThread ?? 'global');
    } else {
      this.renderAll();
    }
    if (!wasVisible) this.onOpenChange?.(true);
  }

  close(): void {
    if (!this._visible) return;
    this._visible = false;
    this.root.style.display = 'none';
    this.closeProfile();
    this.input.blur();
    this.onOpenChange?.(false);
  }

  toggle(): void {
    this._visible ? this.close() : this.open();
  }

  /** Open (or jump to) the DM conversation with a pilot. */
  openDM(partnerName: string): void {
    const key = this.dmKey(partnerName);
    this.ensureThread(key, partnerName);
    this.open(key);
  }

  /** Feed a live ChatReceive from the network layer. */
  addIncoming(payload: {
    senderId: string;
    senderName: string;
    channel: ChatChannel;
    content: string;
    timestamp: number;
    targetName?: string;
    localPlayerId?: string;
    premium?: boolean;
  }): void {
    const fromLocal =
      (!!payload.localPlayerId && payload.senderId === payload.localPlayerId) ||
      (!!this.localName &&
        payload.senderName.toLowerCase() === this.localName.toLowerCase());

    let thread: ThreadState;
    if (payload.channel === ChatChannel.Global) {
      thread = this.ensureThread('global');
    } else if (payload.channel === ChatChannel.Sector || payload.channel === ChatChannel.Local) {
      thread = this.ensureThread('sector');
    } else if (payload.channel === ChatChannel.Direct) {
      const partner = fromLocal ? (payload.targetName ?? '') : payload.senderName;
      if (!partner) return;
      thread = this.ensureThread(this.dmKey(partner), partner);
    } else {
      // System / Fleet noise stays in the corner chatbox.
      return;
    }

    const added = this.pushLine(thread, {
      senderName: payload.senderName,
      content: payload.content,
      timestamp: payload.timestamp,
      fromLocal,
      premium: payload.premium === true,
    });
    if (!added) return;

    if (!fromLocal && (!this._visible || this.activeThread !== thread.key)) {
      thread.unread++;
    }
    if (this._visible) this.renderAll();
  }

  /** Apply a ChatHistoryData response — merges below any live lines. */
  applyHistory(msg: ChatHistoryDataMsg): void {
    let thread: ThreadState | undefined;
    if (msg.channel === ChatChannel.Global) {
      thread = this.threads.get('global');
    } else if (msg.channel === ChatChannel.Sector) {
      thread = this.threads.get('sector');
    } else if (msg.channel === ChatChannel.Direct && msg.partnerName) {
      thread = this.threads.get(this.dmKey(msg.partnerName));
    }
    if (!thread) return;

    thread.historyLoaded = true;
    for (const entry of msg.messages ?? []) {
      const fromLocal =
        !!this.localName &&
        entry.senderName.toLowerCase() === this.localName.toLowerCase();
      this.pushLine(thread, {
        senderName: entry.senderName,
        content: entry.content,
        timestamp: entry.timestamp,
        fromLocal,
      });
    }
    // History can interleave with live messages received meanwhile.
    thread.lines.sort((a, b) => a.timestamp - b.timestamp);
    if (this._visible) this.renderAll();
  }

  /** Re-render the sidebar when the friends list changes. */
  refreshFriends(): void {
    if (this._visible) this.renderSidebar();
  }

  /* ═══════════════ Rendering ═══════════════ */

  private renderAll(): void {
    this.renderSidebar();
    this.renderMain();
    this.applyMobileLayout();
  }

  private applyMobileLayout(): void {
    if (!this.isMobile) return;
    const showList = this.activeThread === null;
    this.sidebar.style.display = showList ? 'flex' : 'none';
    this.main.style.display = showList ? 'none' : 'flex';
  }

  private renderSidebar(): void {
    this.sidebarList.innerHTML = '';

    this.sidebarList.appendChild(this.sectionLabel(t('chat.channelsHeading')));
    this.sidebarList.appendChild(this.buildChannelRow('global', t('chat.global')));
    this.sidebarList.appendChild(this.buildChannelRow('sector', t('chat.sector')));

    this.sidebarList.appendChild(this.sectionLabel(t('chat.dmsHeading')));

    // One row per friend + per DM thread with a non-friend (e.g. an
    // admin who messaged us, or a removed friend with history).
    const friends = (this.callbacks?.getFriends() ?? []).slice();
    const listed = new Set<string>();
    friends.sort((a, b) => {
      if (a.online !== b.online) return a.online ? -1 : 1;
      return a.name.localeCompare(b.name);
    });

    let anyDm = false;
    for (const friend of friends) {
      listed.add(this.dmKey(friend.name));
      this.sidebarList.appendChild(this.buildDmRow(friend.name, friend.online));
      anyDm = true;
    }
    for (const [key, thread] of this.threads) {
      if (thread.channel !== ChatChannel.Direct) continue;
      if (listed.has(key) || !thread.partnerName) continue;
      this.sidebarList.appendChild(this.buildDmRow(thread.partnerName, null));
      anyDm = true;
    }

    if (!anyDm) {
      this.sidebarList.appendChild(
        this.el('div', {
          color: COLORS.dimText,
          fontSize: '10px',
          textTransform: 'none',
          lineHeight: '1.5',
          padding: '6px 8px',
        }, t('chat.noConversations')),
      );
    }
  }

  private sectionLabel(label: string): HTMLElement {
    return this.el('div', {
      fontSize: '10px',
      color: COLORS.secondary,
      letterSpacing: '2px',
      marginTop: '6px',
      flexShrink: '0',
    }, label);
  }

  private buildChannelRow(key: ThreadKey, label: string): HTMLElement {
    const thread = this.ensureThread(key);
    const color = CHANNEL_COLORS[thread.channel] ?? COLORS.primary;
    return this.buildSidebarRow({
      key,
      label,
      labelColor: color,
      unread: thread.unread,
      dot: null,
    });
  }

  private buildDmRow(partnerName: string, online: boolean | null): HTMLElement {
    const key = this.dmKey(partnerName);
    const thread = this.threads.get(key);
    return this.buildSidebarRow({
      key,
      label: partnerName,
      labelColor: COLORS.primary,
      unread: thread?.unread ?? 0,
      dot: online,
      partnerName,
    });
  }

  private buildSidebarRow(args: {
    key: ThreadKey;
    label: string;
    labelColor: string;
    unread: number;
    /** true/false = online dot; null = no presence info. */
    dot: boolean | null;
    partnerName?: string;
  }): HTMLElement {
    const active = this.activeThread === args.key;
    const row = this.el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: this.isMobile ? '12px 10px' : '8px 10px',
      border: `1px solid ${active ? args.labelColor : COLORS.dim}`,
      background: active ? COLORS.rowHover : COLORS.panelBg,
      cursor: 'pointer',
      flexShrink: '0',
    });

    if (args.dot !== null) {
      row.appendChild(
        this.el('div', {
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: args.dot ? COLORS.primary : COLORS.dim,
          boxShadow: args.dot ? `0 0 6px ${COLORS.primary}` : 'none',
          flexShrink: '0',
        }),
      );
    }

    row.appendChild(
      this.el('div', {
        flex: '1',
        fontSize: this.isMobile ? '13px' : '11px',
        color: args.labelColor,
        letterSpacing: '1px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textShadow: active ? GLOW : 'none',
      }, args.label),
    );

    if (args.unread > 0) {
      row.appendChild(
        this.el('div', {
          fontSize: '9px',
          padding: '1px 6px',
          border: `1px solid ${COLORS.secondary}`,
          color: COLORS.secondary,
          borderRadius: '8px',
          flexShrink: '0',
        }, String(Math.min(99, args.unread))),
      );
    }

    row.addEventListener('click', () => {
      if (args.partnerName) this.ensureThread(args.key, args.partnerName);
      this.selectThread(args.key);
    });
    row.addEventListener('mouseenter', () => {
      if (this.activeThread !== args.key) row.style.background = COLORS.rowHover;
    });
    row.addEventListener('mouseleave', () => {
      if (this.activeThread !== args.key) row.style.background = COLORS.panelBg;
    });
    return row;
  }

  private renderMain(): void {
    this.threadHeader.innerHTML = '';
    this.messageArea.innerHTML = '';

    const thread = this.activeThread ? this.threads.get(this.activeThread) : null;

    if (this.isMobile) {
      const back = this.makeBtn(`‹ ${t('chat.back')}`, () => this.selectThread(null), COLORS.dimText as string);
      this.threadHeader.appendChild(back);
    }

    if (!thread) {
      this.inputRow.style.visibility = 'hidden';
      this.messageArea.appendChild(
        this.el('div', {
          color: COLORS.dimText,
          fontSize: '11px',
          textTransform: 'none',
          padding: '20px 8px',
          textAlign: 'center',
        }, t('chat.selectConversation')),
      );
      return;
    }

    this.inputRow.style.visibility = 'visible';

    const headerColor = CHANNEL_COLORS[thread.channel] ?? COLORS.primary;
    const title =
      thread.channel === ChatChannel.Global ? t('chat.global')
      : thread.channel === ChatChannel.Sector ? t('chat.sector')
      : thread.partnerName ?? t('chat.direct');
    const titleEl = this.el('div', {
      fontSize: '13px',
      color: headerColor,
      letterSpacing: '2px',
      textShadow: `0 0 4px ${headerColor}`,
      ...(thread.channel === ChatChannel.Direct ? { cursor: 'pointer' } : {}),
    }, title);
    if (thread.channel === ChatChannel.Direct && thread.partnerName) {
      const partner = thread.partnerName;
      titleEl.addEventListener('click', () => this.openProfile(partner));
    }
    this.threadHeader.appendChild(titleEl);

    if (thread.lines.length === 0) {
      this.messageArea.appendChild(
        this.el('div', {
          color: COLORS.dimText,
          fontSize: '10px',
          textTransform: 'none',
        }, thread.historyLoaded ? t('chat.empty') : t('chat.loadingHistory')),
      );
      return;
    }

    for (const line of thread.lines) {
      this.messageArea.appendChild(this.renderLine(thread, line));
    }
    this.messageArea.scrollTop = this.messageArea.scrollHeight;
  }

  private renderLine(thread: ThreadState, line: StoredLine): HTMLElement {
    const row = this.el('div', {
      fontSize: this.isMobile ? '12px' : '11px',
      lineHeight: '1.5',
      textTransform: 'none',
      wordBreak: 'break-word',
      letterSpacing: '0.5px',
    });

    const time = new Date(line.timestamp);
    const hh = String(time.getHours()).padStart(2, '0');
    const mm = String(time.getMinutes()).padStart(2, '0');
    row.appendChild(
      this.el('span', {
        color: COLORS.dimText,
        marginRight: '6px',
        fontSize: '9px',
      }, `${hh}:${mm}`),
    );

    if (line.premium) {
      row.appendChild(
        this.el('span', {
          color: '#ffd700',
          textShadow: '0 0 4px rgba(255,215,0,0.6)',
          marginRight: '2px',
        }, '★'),
      );
    }

    const channelColor = CHANNEL_COLORS[thread.channel] ?? COLORS.primary;
    const name = this.el('span', {
      color: line.fromLocal ? channelColor : COLORS.primary,
      marginRight: '6px',
      cursor: 'pointer',
      textDecoration: 'underline',
      textDecorationColor: 'rgba(0,255,65,0.25)',
    }, `${line.senderName}:`);
    const senderName = line.senderName;
    name.addEventListener('click', () => this.openProfile(senderName));
    row.appendChild(name);

    row.appendChild(
      this.el('span', { color: 'rgba(0,255,65,0.85)' }, line.content),
    );
    return row;
  }

  /* ═══════════════ Compose ═══════════════ */

  private submit(): void {
    const thread = this.activeThread ? this.threads.get(this.activeThread) : null;
    if (!thread) return;
    const raw = this.input.value.trim();
    if (!raw) return;
    this.input.value = '';
    if (thread.channel === ChatChannel.Direct) {
      if (!thread.partnerName) return;
      this.callbacks?.sendMessage(raw, ChatChannel.Direct, thread.partnerName);
    } else {
      this.callbacks?.sendMessage(raw, thread.channel);
    }
    // The server echoes the message back on the same channel, which is
    // what re-renders the transcript — no local echo needed.
    this.input.focus();
  }

  /* ═══════════════ Profile card ═══════════════ */

  openProfile(name: string): void {
    const trimmed = name.trim();
    if (!trimmed) return;
    this.profileFor = trimmed.toLowerCase();
    this.renderProfileLoading(trimmed);
    this.profileOverlay.style.display = 'flex';
    this.callbacks?.requestProfile(trimmed);
  }

  closeProfile(): void {
    this.profileFor = null;
    this.profileOverlay.style.display = 'none';
    this.profileOverlay.innerHTML = '';
  }

  /** Apply a PlayerProfileData response from the server. */
  applyProfile(msg: PlayerProfileDataMsg): void {
    if (this.profileFor === null) return;
    const matches =
      msg.name.toLowerCase() === this.profileFor ||
      (msg.key !== undefined && msg.key === this.profileFor);
    if (!matches) return;
    this.renderProfileCard(msg);
  }

  private buildProfileShell(): HTMLElement {
    this.profileOverlay.innerHTML = '';
    const card = this.el('div', {
      width: 'min(340px, 86vw)',
      background: COLORS.bg,
      border: `1px solid ${COLORS.dim}`,
      boxShadow: BOX_GLOW,
      padding: '14px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    });
    this.profileOverlay.appendChild(card);
    return card;
  }

  private profileHeader(card: HTMLElement, title: string): void {
    const head = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: `1px solid ${COLORS.dim}`,
      paddingBottom: '6px',
    });
    head.appendChild(
      this.el('div', {
        fontSize: '12px',
        letterSpacing: '2px',
        color: COLORS.secondary,
      }, title),
    );
    head.appendChild(this.makeBtn('X', () => this.closeProfile(), COLORS.danger));
    card.appendChild(head);
  }

  private renderProfileLoading(name: string): void {
    const card = this.buildProfileShell();
    this.profileHeader(card, t('profile.title'));
    card.appendChild(
      this.el('div', {
        fontSize: '13px',
        textShadow: GLOW,
        letterSpacing: '2px',
      }, name),
    );
    card.appendChild(
      this.el('div', {
        color: COLORS.dimText,
        fontSize: '10px',
        textTransform: 'none',
      }, t('profile.loading')),
    );
  }

  private renderProfileCard(msg: PlayerProfileDataMsg): void {
    const card = this.buildProfileShell();
    this.profileHeader(card, t('profile.title'));

    if (!msg.found) {
      card.appendChild(
        this.el('div', {
          color: COLORS.dimText,
          fontSize: '11px',
          textTransform: 'none',
          padding: '10px 0',
        }, t('profile.notFound', { name: msg.name })),
      );
      return;
    }

    const nameRow = this.el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    });
    nameRow.appendChild(
      this.el('div', {
        width: '9px',
        height: '9px',
        borderRadius: '50%',
        background: msg.online ? COLORS.primary : COLORS.dim,
        boxShadow: msg.online ? `0 0 6px ${COLORS.primary}` : 'none',
        flexShrink: '0',
      }),
    );
    if (msg.premium) {
      nameRow.appendChild(
        this.el('span', {
          color: '#ffd700',
          textShadow: '0 0 4px rgba(255,215,0,0.6)',
        }, '★'),
      );
    }
    nameRow.appendChild(
      this.el('div', {
        fontSize: '15px',
        textShadow: GLOW,
        letterSpacing: '2px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }, msg.name),
    );
    card.appendChild(nameRow);

    const statusLine = this.el('div', {
      fontSize: '10px',
      color: msg.online ? COLORS.primary : COLORS.dimText,
      letterSpacing: '1px',
    }, msg.online ? t('profile.statusOnline') : t('profile.statusOffline'));
    card.appendChild(statusLine);

    const raceInfo = RACE_INFO[(msg.race ?? 0) as RaceId];
    const infoGrid = this.el('div', {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '4px 12px',
      fontSize: '11px',
    });
    const infoCell = (label: string, value: string) => {
      const cell = this.el('div', { display: 'flex', gap: '6px' });
      cell.appendChild(this.el('span', { color: COLORS.dimText }, label));
      cell.appendChild(this.el('span', { color: COLORS.primary }, value));
      infoGrid.appendChild(cell);
    };
    infoCell(t('profile.level'), String(msg.level ?? 1));
    infoCell(t('profile.race'), raceInfo?.name ?? '—');
    if (msg.stats) {
      infoCell(t('profile.kills'), msg.stats.totalKills.toLocaleString());
      infoCell(t('profile.missions'), msg.stats.missionsCompleted.toLocaleString());
      infoCell(t('profile.sectors'), msg.stats.sectorsDiscovered.toLocaleString());
    }
    card.appendChild(infoGrid);

    if (msg.isSelf) {
      card.appendChild(
        this.el('div', {
          color: COLORS.dimText,
          fontSize: '10px',
          textTransform: 'none',
        }, t('profile.self')),
      );
      return;
    }

    const actions = this.el('div', {
      display: 'flex',
      gap: '8px',
      justifyContent: 'flex-end',
      marginTop: '4px',
      flexWrap: 'wrap',
    });

    if (msg.isFriend) {
      actions.appendChild(
        this.el('div', {
          fontSize: '10px',
          color: COLORS.primary,
          border: `1px solid ${COLORS.dim}`,
          padding: '5px 10px',
          letterSpacing: '1px',
        }, t('profile.friendBadge')),
      );
      actions.appendChild(
        this.makeBtn(t('profile.message'), () => {
          this.closeProfile();
          this.openDM(msg.name);
        }, COLORS.primary),
      );
    } else if (msg.requestPending) {
      actions.appendChild(
        this.el('div', {
          fontSize: '10px',
          color: COLORS.secondary,
          border: `1px solid ${COLORS.secondary}`,
          padding: '5px 10px',
          letterSpacing: '1px',
        }, t('profile.requestPending')),
      );
    } else {
      const addBtn = this.makeBtn(t('profile.addFriend'), () => {
        this.callbacks?.onAddFriend(msg.name);
        // Optimistic swap to "pending" — the authoritative
        // FriendRequestsData snapshot follows right behind.
        this.renderProfileCard({ ...msg, requestPending: true });
      }, COLORS.secondary);
      actions.appendChild(addBtn);
    }
    card.appendChild(actions);
  }

  /* ═══════════════ Small helpers ═══════════════ */

  private makeBtn(label: string, onClick: () => void, color: string): HTMLElement {
    const btn = this.el('button', {
      fontFamily: FONT,
      fontSize: this.isMobile ? '12px' : '10px',
      padding: this.isMobile ? '8px 14px' : '5px 10px',
      background: 'transparent',
      border: `1px solid ${color}`,
      color,
      cursor: 'pointer',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      transition: 'all 0.15s',
      flexShrink: '0',
      touchAction: 'manipulation',
    }, label);
    btn.addEventListener('mouseenter', () => {
      btn.style.background = color;
      btn.style.color = '#000';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = 'transparent';
      btn.style.color = color;
    });
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
    return btn;
  }
}
